import React from 'react';
import { formatMoney, formatNumber, plural } from '../../../utils/formatters';
import { Delta, PREV_LABELS, fmtPct } from './shared';
import { WarningIcon, ArrowRightIcon } from './icons';

/**
 * Четыре главных числа периода. Каждое — один раз на странице, с изменением
 * к такому же прошлому периоду. Без декоративных графиков внутри плиток:
 * раньше донат, гейдж и спарклайн повторяли то, что ниже показано подробно.
 */
const KpiTiles = ({ data, previous, days, vsLabel, busy }) => {
  const t = data.totals;
  const n = data.newcomers;
  const pt = previous?.totals;
  const pn = previous?.newcomers;
  const vs = vsLabel || PREV_LABELS[days];

  const tiles = [
    {
      key: 'guests',
      label: 'Гостей с картой',
      value: formatNumber(t.guests),
      delta: <Delta current={t.guests} previous={pt?.guests} title={vs} />,
      sub: `${formatNumber(t.visits)} ${plural(t.visits, 'визит', 'визита', 'визитов')} · ${formatMoney(t.revenue)}`,
    },
    {
      key: 'repeat',
      label: 'Приходили повторно',
      value: fmtPct(t.repeat_rate),
      delta: <Delta current={t.repeat_rate} previous={pt?.repeat_rate} kind="pp" title={vs} />,
      sub: t.avg_days_between
        ? `в среднем раз в ${t.avg_days_between} дн.`
        : 'гостей с 2+ визитами за период',
    },
    {
      key: 'newcomers',
      label: 'Новички вернулись',
      value: fmtPct(n.conversion),
      delta: <Delta current={n.conversion} previous={pn?.conversion} kind="pp" title={vs} />,
      sub: `${formatNumber(n.returned)} из ${formatNumber(n.new_guests)} новых`,
    },
    {
      key: 'check',
      label: 'Средний чек визита',
      value: formatMoney(t.avg_check),
      delta: <Delta current={t.avg_check} previous={pt?.avg_check} title={vs} />,
      sub: `${formatMoney(t.revenue_per_guest)} на гостя за период`,
    },
  ];

  return (
    <div className={`crma-kpis ${busy ? 'is-busy' : ''}`}>
      {tiles.map((tile) => (
        <div className="crma-kpi" key={tile.key}>
          <div className="crma-kpi-label">{tile.label}</div>
          <div className="crma-kpi-row">
            <span className="crma-kpi-value">{tile.value}</span>
            {tile.delta}
          </div>
          <div className="crma-kpi-sub">{tile.sub}</div>
        </div>
      ))}
      {previous && <div className="crma-kpis-note">Стрелки — {vs}</div>}
    </div>
  );
};

const PROGRAM_PREV_LABELS = { 7: 'к прошлым 7 дням', 30: 'к прошлым 30 дням', 365: 'к прошлому году' };

const fmtDay = (iso) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
const fmtShort = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
const fmtWindow = (w) => (w ? `${fmtShort(w[0])} – ${fmtShort(w[1])}` : '');

/**
 * Главные числа по гостям программы лояльности iiko.biz. Стрелки к прошлому
 * периоду — по журналу визитов (фаза 3): и текущее, и прошлое окно из одного
 * источника (currentJournal / previous). Пока журнал грузится или не
 * покрывает прошлое окно — честная подпись вместо стрелок.
 */
export const ProgramKpiTiles = ({ summary, previous, currentJournal, journal, days, vsLabel, busy }) => {
  const n = summary.newcomers;
  const cur = currentJournal;
  const prev = previous;
  const vs = vsLabel || PROGRAM_PREV_LABELS[days];
  const tiles = [
    {
      key: 'active',
      label: 'Гостей приходило',
      value: formatNumber(summary.active),
      delta: prev && <Delta current={cur.active} previous={prev.active} title={vs} />,
      sub: `${formatMoney(summary.revenue)} за период`,
    },
    {
      key: 'returning',
      label: 'Были и раньше',
      value: fmtPct(summary.returning_share),
      delta: prev && <Delta current={cur.returning_share} previous={prev.returning_share} kind="pp" title={vs} />,
      sub: `${formatNumber(summary.returning)} из ${formatNumber(summary.active)} — не новички`,
    },
    {
      key: 'newcomers',
      label: 'Новички вернулись',
      value: fmtPct(n.conversion),
      delta: prev && <Delta current={cur.newcomers.conversion} previous={prev.newcomers.conversion} kind="pp" title={vs} />,
      sub: `${formatNumber(n.returned)} из ${formatNumber(n.new_guests)} новых`,
    },
    {
      key: 'per-guest',
      label: 'Траты на гостя',
      value: formatMoney(summary.per_guest),
      delta: prev && <Delta current={cur.per_guest} previous={prev.per_guest} title={vs} />,
      sub: 'за период, в среднем',
    },
  ];

  let note;
  if (vsLabel) {
    // Свой период: окна ровно такие, как выбрал пользователь, сегодня может входить
    note = prev
      ? `Стрелки — ${fmtWindow(journal?.current_window)} к ${fmtWindow(journal?.previous_window)}, по журналу визитов`
      : `Сравнения нет: такой же период перед выбранным начинается раньше истории визитов (она есть с ${fmtDay(journal?.covered_from || '2025-01-01')})`;
  } else if (prev) {
    // Окна журнала заканчиваются вчера (сегодняшние заказы попадают в него
    // на следующий день) — пишем даты, чтобы было видно, что с чем сравнили.
    note = journal?.current_window
      ? `Стрелки — ${fmtWindow(journal.current_window)} к ${fmtWindow(journal.previous_window)}, по журналу визитов (сегодня не входит — заказы дня попадают в журнал на следующий день)`
      : `Стрелки — ${vs}, по журналу визитов`;
  } else if (journal && !journal.complete) {
    note = journal.total
      ? `История визитов загружается: ${formatNumber(journal.loaded)} из ${formatNumber(journal.total)} гостей — потом появится сравнение с прошлым периодом`
      : 'Сравнение с прошлым периодом появится, когда загрузится история визитов';
  } else if (journal?.compare_from) {
    note = `Сравнение за этот период появится ${fmtDay(journal.compare_from)}: история визитов есть с ${fmtDay(journal.covered_from)}`;
  }

  return (
    <div className={`crma-kpis ${busy ? 'is-busy' : ''}`}>
      {tiles.map((tile) => (
        <div className="crma-kpi" key={tile.key}>
          <div className="crma-kpi-label">{tile.label}</div>
          <div className="crma-kpi-row">
            <span className="crma-kpi-value">{tile.value}</span>
            {tile.delta}
          </div>
          <div className="crma-kpi-sub">{tile.sub}</div>
        </div>
      ))}
      {note && <div className="crma-kpis-note">{note}</div>}
    </div>
  );
};

/**
 * «Кого теряем прямо сейчас» — по статусам базы гостей (пауза дольше
 * обычной для этого гостя). Деньги — годовая выручка этих гостей: сколько
 * стоит их не вернуть. Кнопки ведут в список с тем же фильтром.
 */
export const RiskStrip = ({ summary, onOpenGuests }) => {
  if (!summary) return null;
  const risk = summary.risk || { guests: 0, revenue: 0 };
  const lost = summary.lost || { guests: 0, revenue: 0 };
  if (!risk.guests && !lost.guests) return null;

  return (
    <div className="crma-risk">
      <span className="crma-risk-icon"><WarningIcon size={18} /></span>
      <div className="crma-risk-text">
        <strong>
          {formatNumber(risk.guests)} под риском и {formatNumber(lost.guests)} потерянных
        </strong>
        <span>
          Вместе они потратили у вас {formatMoney(risk.revenue + lost.revenue)}. Под риском — те,
          кто пропустил свой обычный интервал между визитами: их ещё реально вернуть.
        </span>
      </div>
      <div className="crma-risk-actions">
        {risk.guests > 0 && (
          <button className="ui-btn-primary" onClick={() => onOpenGuests({ status: 'risk' })}>
            Под риском <ArrowRightIcon size={14} />
          </button>
        )}
        {lost.guests > 0 && (
          <button className="ui-btn-ghost" onClick={() => onOpenGuests({ status: 'lost' })}>
            Потерянные <ArrowRightIcon size={14} />
          </button>
        )}
      </div>
    </div>
  );
};

export default KpiTiles;
